/**
 * NBPLCContext — Single source of truth for Nigeria Breweries workspace.
 * Separate from DangoteContext — no shared state.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import client from "@/api/client";

const Ctx = createContext(null);
export const useNBPLC = () => {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useNBPLC must be used within NBPLCProvider");
  return ctx;
};

export function NBPLCProvider({ children }) {
  const [periods,           setPeriods]           = useState([]);
  const [selectedPeriod,    setSelectedPeriodState]= useState(null);
  const [consolidated,      setConsolidated]      = useState(null);
  const [loading,           setLoading]           = useState(true);
  // VB cache — keyed by period_id
  const [vbCache,           setVbCache]           = useState({});
  const [vbLoading,         setVbLoading]         = useState(false);

  // Load periods on mount — default to approved period
  useEffect(() => {
    client.get("/nbplc/periods").then(r => {
      const ps = r.data || [];
      setPeriods(ps);
      const active =
        ps.find(p => p.status === "approved") ||
        ps.find(p => p.status === "submitted") ||
        ps.find(p => p.status === "draft")    ||
        ps[0];
      if (active) setSelectedPeriodState(active.period_id);
    }).catch(() => setLoading(false));
  }, []);

  // Load consolidated when period changes
  const loadConsolidated = useCallback((pid) => {
    if (!pid) return;
    setLoading(true);
    client.get(`/nbplc/consolidated/${pid}`)
      .then(r => { setConsolidated(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { if (selectedPeriod) loadConsolidated(selectedPeriod); }, [selectedPeriod, loadConsolidated]);

  const period = useMemo(() => periods.find(p => p.period_id === selectedPeriod) || null, [periods, selectedPeriod]);

  const setSelectedPeriod = useCallback((pid) => {
    setSelectedPeriodState(pid);
    setVbCache(prev => {
      const next = { ...prev }; delete next[pid]; return next;
    });
  }, []);

  // VB cache
  const loadVb = useCallback((pid) => {
    if (!pid || vbCache[pid]) return;
    setVbLoading(true);
    client.get(`/nbplc/value-bridge/${pid}`)
      .then(r => { setVbCache(prev => ({ ...prev, [pid]: r.data })); setVbLoading(false); })
      .catch(() => setVbLoading(false));
  }, [vbCache]);

  useEffect(() => { if (selectedPeriod) loadVb(selectedPeriod); }, [selectedPeriod, loadVb]);

  const invalidateVb = useCallback((pid) => {
    const p = pid || selectedPeriod;
    if (!p) return;
    setVbLoading(true);
    client.get(`/nbplc/value-bridge/${p}`)
      .then(r => { setVbCache(prev => ({ ...prev, [p]: r.data })); setVbLoading(false); })
      .catch(() => setVbLoading(false));
  }, [selectedPeriod]);

  const refresh = useCallback(() => { if (selectedPeriod) loadConsolidated(selectedPeriod); }, [selectedPeriod, loadConsolidated]);

  return (
    <Ctx.Provider value={{
      periods, selectedPeriod, setSelectedPeriod, period,
      consolidated, loading, refresh,
      vbCache, vbLoading, invalidateVb,
      // Derived: evidence confidence (computed from consolidated)
      evidencePct: consolidated?.evidence_completeness_pct ?? 0,
    }}>
      {children}
    </Ctx.Provider>
  );
}
